import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Badge, Button } from '@scramble/ui';
import { useIsMobile } from '@scramble/hooks';
import { useVeriffStatus } from '@/hooks/shared/useVeriffStatus';
import { useGetMe } from '@/hooks/useGetMe';
import { useGetBankAccounts } from '@/query/queries/cash/useGetBankAccounts';
import NotificationBlock from '@/containers/NotificationBlock';
import { Preloader } from '@/components/Preloader';
import { NOTIFICATIONS as NOTIFICATIONS_T } from '@/components/Notification/list';
import { BankAccountUnderReviewModal } from '@/modals/BankAccountUnderReviewModal';
import { BankAccountRemoveModal } from '@/modals/BankAccountRemoveModal';
import { lockBodyScroll, unlockBodyScroll } from '@/helpers/scroll';
import { MODAL_IDS } from '@/kit/Modal/keys';
import { useModal } from '@/providers/modal';
import { BANK_ACCOUNT_REVIEW_STATUSES } from '../BankAccountForm/form/schema';
import { BankAccountForm } from '../BankAccountForm';
import { EmptyState } from '../EmptyState';
import IconEdit from '@scramble/design-system/icons/edit-03.svg';
import IconRemove from '@scramble/design-system/icons/trash-01.svg';
import s from './index.module.scss';

export const LinkedBankAccounts = () => {
  const { isVerified } = useGetMe();
  const { openSingleModal } = useModal();
  const isMobile = useIsMobile();
  const { ready, isVeriffSubmitted } = useVeriffStatus();
  const { data: accounts, isLoading } = useGetBankAccounts();
   const { t } = useTranslation();

  const [searchParams, setSearchParams] = useSearchParams();

  const [formState, setFormState] = useState({
    show: searchParams.get('create') === 'true',
    account: null,
  });

  useEffect(() => {
    if (searchParams.get('create') === 'true') {
      setSearchParams({}, { replace: true });
    }
  }, []);

  useEffect(() => {
    formState.show ? lockBodyScroll() : unlockBodyScroll();
    return () => {
      unlockBodyScroll();
    };
  }, [formState.show]);

  const NOTIFICATIONS = NOTIFICATIONS_T(t);
  const notifications = [];

  if (ready && isVeriffSubmitted) {
    notifications.push(
      NOTIFICATIONS.checking_transactions_available_to_verified_users(),
    );
  } else if (!isVerified) {
    notifications.push(
      NOTIFICATIONS.bank_accounts_available_to_verified_users(),
    );
  }

  const onAddAccount = () => {
    setFormState({ show: true, account: null });
  };

  const onEditAccount = account => {
    setFormState({ show: true, account });
  };

  const onRemoveAccount = account => {
    setFormState({ show: false, account });
    openSingleModal(MODAL_IDS.bank_account_remove_modal);
  };

  const getBadgeProps = (status) => {
    switch (status) {
      case BANK_ACCOUNT_REVIEW_STATUSES.pending:
        return {
          label: t('bank_accounts:review_status.pending'),
          type: 'info',
        };
      case BANK_ACCOUNT_REVIEW_STATUSES.approved:
        return {
          label: t('bank_accounts:review_status.approved'),
          type: 'success',
        };
      case BANK_ACCOUNT_REVIEW_STATUSES.rejected:
        return {
          label: t('bank_accounts:review_status.rejected'),
          type: 'danger',
        };
      case BANK_ACCOUNT_REVIEW_STATUSES.deleted:
        return {
          label: t('bank_accounts:review_status.deleted'),
          type: 'danger',
        };
      default:
        return {
          label: t('bank_accounts:review_status.pending'),
          type: 'info',
        };
    }
  }

  if (isLoading) return <Preloader className={s.preloader} />

    return (
      <div className={s.wrapper}>
        <NotificationBlock
          className={s.notifications}
          notifications={notifications}
        />

        {isVerified && (
          <>
            <div className={s.header}>
              <div className="f-headline-xs f-500">
                {t('bank_accounts:linked_bank_accounts.title')}
              </div>
              <Button
                label={t('bank_accounts:linked_bank_accounts.add_account')}
                theme="neutral"
                appearance="filled"
                size="md"
                onClick={onAddAccount}
              />
            </div>

            {accounts?.length ? (
              <div className={s.accounts}>
                {accounts.map(account => (
                  <div key={account.id} className={s.account}>
                    <div className={s.account_info}>
                      <div className="f-body-sm f-500">
                        {account.account_number}
                      </div>
                      <div className={s.account_status}>
                        <Badge
                          {...getBadgeProps(account.review_status)}
                          appearance="tonal"
                          size={isMobile ? 'small' : 'medium'}
                        />
                      </div>
                    </div>
                    <div className={s.account_controls}>
                      {[
                        BANK_ACCOUNT_REVIEW_STATUSES.pending,
                        BANK_ACCOUNT_REVIEW_STATUSES.rejected,
                      ].includes(account.review_status) && (
                        <button
                          type="button"
                          onClick={() => onEditAccount(account)}
                        >
                          <img
                            src={IconEdit}
                            alt="edit"
                            width={20}
                            height={20}
                          />
                        </button>
                      )}
                      <button
                        type="button"
                        onClick={() => onRemoveAccount(account)}
                      >
                        <img
                          src={IconRemove}
                          alt="remove"
                          width={20}
                          height={20}
                        />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <EmptyState />
            )}

            {formState.show && (
              <BankAccountForm
                accountId={formState.account?.id}
                onBack={() => setFormState({ show: false, account: null })}
              />
            )}

            <>
              <BankAccountUnderReviewModal />
              <BankAccountRemoveModal
                account={formState.account}
                accountNumber
              />
            </>
          </>
        )}
      </div>
    );
};